import { useModal } from '@ebay/nice-modal-react';
import {
  ClientEvent,
  EventType,
  KnownMembership,
  MatrixEvent,
  Room,
  RoomMember,
  RoomMemberEvent,
  RoomStateEvent,
  RoomType,
} from 'matrix-js-sdk/src/matrix';
import { useEffect, useState } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useLatest from 'hooks/useLatest';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';
import { SetState } from 'utils/types';

import { buildWorkspaceSnapshot } from './utils';
import { WorkspacesState } from '../WorkspacesDropdown/utils';
import LeaveAndAssignAdminModal from '../WorkspaceSettingsModal/GeneralTab/ManagementSettingsSection/LeaveAndAssignAdminModal';
import WorkspaceSettingsModal from '../WorkspaceSettingsModal/WorkspaceSettingsModal';

const useWorkspacesState = (workspaceId: string | null, setWorkspaceId: SetState<string | null>) => {
  const { matrixClient, hasReachedSyncReadyState } = useMatrixContext({ requireDefinedMatrixClient: false });
  const workspaceIdRef = useLatest(workspaceId);
  const { remove: removeLeaveAndAssignAdminModal } = useModal(LeaveAndAssignAdminModal);
  const { remove } = useModal(WorkspaceSettingsModal);
  const [workspacesState, setWorkspacesState] = useState<WorkspacesState>(() => {
    if (!matrixClient) return { workspaces: {}, rootWorkspaces: [] };

    return buildWorkspaceSnapshot(matrixClient);
  });

  useEffect(() => {
    if (!matrixClient) return;

    const updateWorkspaceState = () => setWorkspacesState(buildWorkspaceSnapshot(matrixClient));

    const handleStateEventUpdate = (event: MatrixEvent) => {
      const eventType = event.getType();

      const isRelevantEvent = [
        CustomMeedioMatrixEvents.SPACE_META,
        EventType.SpaceChild,
        EventType.SpaceParent,
        EventType.RoomAvatar,
        EventType.RoomCanonicalAlias,
        EventType.RoomName,
        EventType.RoomCanonicalAlias,
        EventType.RoomCreate,
        EventType.RoomMember,
      ].includes(eventType);

      if (!isRelevantEvent) return;

      if (eventType === EventType.RoomMember) {
        const content = event.getContent() as { membership?: string; displayname?: string };
        const prevContent = event.getPrevContent() as { membership?: string; displayname?: string };
        // Pure display-name rename (membership unchanged) — no structural workspace change; skip rebuild.
        // Avoids N redundant buildWorkspaceSnapshot runs when a rename fans out to every joined room (#8254).
        if (content.membership === prevContent?.membership && content.displayname !== prevContent?.displayname) {
          return;
        }
      }

      updateWorkspaceState();
    };

    const handleMembershipChange = async (_ev: MatrixEvent, member: RoomMember) => {
      const membership = member.membership;
      const myUserId = matrixClient.getUserId();

      if (!myUserId || !membership || member.userId !== myUserId) return;

      const room = matrixClient.getRoom(member.roomId);
      const isWorkspaceLeave = [KnownMembership.Leave, KnownMembership.Ban].includes(membership as KnownMembership);
      const isJoinOrLeave = KnownMembership.Join === membership || isWorkspaceLeave;
      const isSpace = room && room.getType() === RoomType.Space;

      if (!isSpace || !isJoinOrLeave) return;

      //NOTE: remove workspace selection if is leaving currently selected workspace
      const isLeavingCurrentWorkspace = isWorkspaceLeave && workspaceId === member.roomId;
      if (isLeavingCurrentWorkspace) {
        remove();
        removeLeaveAndAssignAdminModal();
        setWorkspaceId(null);
      }

      updateWorkspaceState();
    };

    const handleRoomEvent = (room: Room) => {
      if (room.getType() === RoomType.Space) updateWorkspaceState();
    };

    matrixClient.on(ClientEvent.Room, handleRoomEvent);
    matrixClient.on(RoomStateEvent.Events, handleStateEventUpdate);
    matrixClient.on(RoomMemberEvent.Membership, handleMembershipChange);

    return () => {
      matrixClient.off(ClientEvent.Room, handleRoomEvent);
      matrixClient.off(RoomStateEvent.Events, handleStateEventUpdate);
      matrixClient.off(RoomMemberEvent.Membership, handleMembershipChange);
    };
  }, [matrixClient, remove, removeLeaveAndAssignAdminModal, setWorkspaceId, workspaceId]);

  useEffect(() => {
    if (!matrixClient || !hasReachedSyncReadyState) return;

    const snapshot = buildWorkspaceSnapshot(matrixClient);
    setWorkspacesState(snapshot);

    if (workspaceIdRef.current && !snapshot.workspaces[workspaceIdRef.current]) setWorkspaceId(null);
  }, [hasReachedSyncReadyState, matrixClient, setWorkspaceId, workspaceIdRef]);

  return workspacesState;
};

export default useWorkspacesState;
